// src/services/toolUsage/toolUsageService.ts
// Tool-usage tracking — data layer for "Recently Used Tools" / "Suggested
// Tools". Standalone table (public.tool_usage_events, see
// supabase/migrations/20260801000000_tool_usage_events.sql), RLS-scoped to
// auth.uid() the same way GroupOS's cohortService.ts is.
//
// HONESTY BOUNDARY — read this before touching this file:
// This data is read-only convenience/discovery UI. It must NEVER be wired
// into any eligibility, points, readiness, or success-score calculation
// anywhere in the app. This session spent hours finding and removing an
// `activatedTools.length * N` "tool activation bonus" pattern that inflated
// real scores just because a user had opened other tools (CLAUDE.md working
// rule #5). Do not import recordToolUsage/listRecentToolUsage/
// listUsedToolIds into any scoring function, calculator, or assessment
// engine. If you're tempted to pass a usage count into a score "just to
// see" — don't. That boundary is absolute.
//
// ARCHITECTURAL BOUNDARY:
// Deliberately standalone from the UPRM spine (docs/ARCHITECTURE.md), same
// posture as GroupOS. This file never writes to journey_events /
// journey_states and never calls UPRMLifecycleService / JourneyStateService.
import { supabase } from '@/lib/supabase';

export interface ToolUsageEvent {
  id: string;
  user_id: string;
  tool_id: string;
  tool_name: string;
  tool_path: string;
  country: string | null;
  category: string | null;
  used_at: string;
}

export interface RecordToolUsageInput {
  toolId: string;
  toolName: string;
  toolPath: string;
  country?: string | null;
  category?: string | null;
}

// Fire-and-forget insert. Never throws — the caller (the mount hook / route
// wrapper) shouldn't let a logging failure affect the page being logged.
// Silently no-ops if nobody is signed in (nothing to attribute the row to).
export async function recordToolUsage(input: RecordToolUsageInput): Promise<void> {
  try {
    const { data: authData } = await supabase.auth.getUser();
    const uid = authData?.user?.id;
    if (!uid) return;

    const { error } = await supabase.from('tool_usage_events').insert({
      user_id: uid,
      tool_id: input.toolId,
      tool_name: input.toolName,
      tool_path: input.toolPath,
      country: input.country ?? null,
      category: input.category ?? null,
    });
    if (error) console.error('[toolUsageService] recordToolUsage failed', error);
  } catch (err) {
    console.error('[toolUsageService] recordToolUsage threw', err);
  }
}

// Last N *distinct* tools the signed-in user has opened, most recent first.
// Pulls a bounded recent window and dedupes client-side, keeping each tool's
// most recent occurrence — simplest correct approach with the supabase-js
// query builder (no DISTINCT ON without a dedicated RPC).
export async function listRecentToolUsage(limit = 8): Promise<ToolUsageEvent[]> {
  const { data: authData } = await supabase.auth.getUser();
  const uid = authData?.user?.id;
  if (!uid) return [];

  const { data, error } = await supabase
    .from('tool_usage_events')
    .select('*')
    .eq('user_id', uid)
    .order('used_at', { ascending: false })
    .limit(200);
  if (error) { console.error('[toolUsageService] listRecentToolUsage failed', error); return []; }

  const seen = new Set<string>();
  const distinct: ToolUsageEvent[] = [];
  for (const row of (data ?? []) as ToolUsageEvent[]) {
    if (seen.has(row.tool_id)) continue;
    seen.add(row.tool_id);
    distinct.push(row);
    if (distinct.length >= limit) break;
  }
  return distinct;
}

// Every distinct tool_id the signed-in user has ever used. RLS already
// scopes this to their own rows; the .eq() below is for clarity, not the
// security boundary (same convention as cohortService.listMyMemberships).
export async function listUsedToolIds(): Promise<Set<string>> {
  const { data: authData } = await supabase.auth.getUser();
  const uid = authData?.user?.id;
  if (!uid) return new Set();

  const { data, error } = await supabase
    .from('tool_usage_events')
    .select('tool_id')
    .eq('user_id', uid);
  if (error) { console.error('[toolUsageService] listUsedToolIds failed', error); return new Set(); }
  return new Set((data ?? []).map((r: { tool_id: string }) => r.tool_id));
}

// ── Suggestion heuristic ─────────────────────────────────────────────────
// Deliberately NOT "AI-powered" or cross-user "commonly paired with" — RLS
// scopes every read on this table to the caller's own rows, so no cross-user
// co-occurrence signal exists to draw on without a new SECURITY DEFINER RPC
// (out of scope for v1). Instead: an honest, fully client-computable
// heuristic from the user's own history plus the catalog already in scope on
// the page that renders it (each country's *ToolsEcosystem.tsx keeps its own
// TOOLS array local — pass it in rather than re-importing/duplicating it).
//
// Priority: active tools in categories the user has never touched at all,
// then any other not-yet-used active tool in the same catalog.
export interface CatalogToolLite {
  id: string;
  name: string;
  path: string;
  category: string;
  status?: string; // only 'active' tools are ever suggested
}

export function suggestUnexploredTools(
  catalog: CatalogToolLite[],
  usedCatalogIds: Set<string>,
  limit = 6,
): CatalogToolLite[] {
  const unused = catalog.filter(
    (t) => (t.status ?? 'active') === 'active' && !usedCatalogIds.has(t.id),
  );
  const touchedCategories = new Set(
    catalog.filter((t) => usedCatalogIds.has(t.id)).map((t) => t.category),
  );
  const sorted = [...unused].sort((a, b) => {
    const aTouched = touchedCategories.has(a.category) ? 1 : 0;
    const bTouched = touchedCategories.has(b.category) ? 1 : 0;
    return aTouched - bTouched; // untouched-category tools first
  });
  return sorted.slice(0, limit);
}
